import React, { useEffect, useMemo, useCallback, useState } from "react";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { useCoA } from "../../context/CoAContext";
import "./AccountLedger.css";

const MIRROR_PREFIX = "Mirror — ";

function normalizeMoney(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-US");
}

export default function AccountLedger() {
  const { companyId } = useCompany();
  const { accounts } = useCoA(); // ✅ reuse existing COA cache

  // Filters
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedSource, setSelectedSource] = useState(""); // "" | "Journal" | "Bank"
  const [searchText, setSearchText] = useState("");

  // Data
  const [rows, setRows] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);

  // UI
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Default date range to YTD on mount
  useEffect(() => {
    const year = new Date().getFullYear();
    setFilterStartDate(`${year}-01-01`);
    setFilterEndDate(new Date().toISOString().split("T")[0]);
  }, []);

  /**
   * BANK → Ledger conversion (double-entry, no mirrors required)
   *
   * Each categorized bank tx becomes TWO ledger lines:
   * - categorized account (account_id)
   * - bank account (bank_account_id)
   */
  const convertBankTxToLedgerLines = useCallback(
    (tx) => {
      const amt = normalizeMoney(tx.amount);
      const base = {
        date: tx.date,
        source: "Bank",
        source_id: tx.id,
        description: tx.description,
        meta: {
          bank_account_id: tx.bank_account_id,
          categorized_account_id: tx.account_id,
          bank_type: tx.type,
        },
      };

      const isDebit = String(tx.type).toLowerCase() === "debit"; // money out

      // categorized side
      const catLine = {
        ...base,
        account_id: tx.account_id,
        account_name: tx.account_name || "Unknown",
        debit: isDebit ? amt : 0,
        credit: isDebit ? 0 : amt,
      };

      // bank side
      const bankName =
        accounts?.find((a) => a.id === tx.bank_account_id)?.name ||
        "Bank Account (Unknown)";

      const bankLine = {
        ...base,
        account_id: tx.bank_account_id,
        account_name: bankName,
        debit: isDebit ? 0 : amt,
        credit: isDebit ? amt : 0,
        description: `Bank side — ${tx.description}`,
      };

      return [catLine, bankLine];
    },
    [accounts]
  );

  const fetchLedger = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setErr("");

    try {
      if (!selectedAccountId) {
        setRows([]);
        setOpeningBalance(0);
        setLoading(false);
        return;
      }

      // -------- OPENING BALANCE (before start date) --------

      // Journals opening
      let jOpenQ = supabase
        .from("journal_entries")
        .select("money_in, money_out, date")
        .eq("company_id", companyId)
        .eq("account_id", selectedAccountId);

      if (filterStartDate) jOpenQ = jOpenQ.lt("date", filterStartDate);

      // Bank opening (categorized only, exclude mirrors)
      let bOpenQ = supabase
        .from("bank_transactions")
        .select(
          "id, date, description, amount, type, account_id, account_name, bank_account_id"
        )
        .eq("company_id", companyId)
        .eq("status", "Categorized")
        .not("description", "ilike", `${MIRROR_PREFIX}%`);

      if (filterStartDate) bOpenQ = bOpenQ.lt("date", filterStartDate);

      // -------- PERIOD ROWS (within date range) --------

      // Journals
      let jQ = supabase
        .from("journal_entries")
        .select("id, date, description, money_in, money_out")
        .eq("company_id", companyId)
        .eq("account_id", selectedAccountId);

      // Bank (categorized only, exclude mirrors)
      let bQ = supabase
        .from("bank_transactions")
        .select(
          "id, date, description, amount, type, account_id, account_name, bank_account_id"
        )
        .eq("company_id", companyId)
        .eq("status", "Categorized")
        .not("description", "ilike", `${MIRROR_PREFIX}%`);

      if (filterStartDate) {
        jQ = jQ.gte("date", filterStartDate);
        bQ = bQ.gte("date", filterStartDate);
      }
      if (filterEndDate) {
        jQ = jQ.lte("date", filterEndDate);
        bQ = bQ.lte("date", filterEndDate);
      }

      const [
        { data: jOpen = [], error: jOpenErr },
        { data: bOpen = [], error: bOpenErr },
        { data: jData = [], error: jErr },
        { data: bData = [], error: bErr },
      ] = await Promise.all([jOpenQ, bOpenQ, jQ, bQ]);

      if (jOpenErr || bOpenErr || jErr || bErr) {
        throw jOpenErr || bOpenErr || jErr || bErr;
      }

      // Compute opening
      let open = 0;

      // Journals opening: (debit - credit)
      for (const r of jOpen) {
        open += normalizeMoney(r.money_in) - normalizeMoney(r.money_out);
      }

      // Bank opening: convert each tx to 2 lines and apply only if account matches
      for (const tx of bOpen) {
        const [catLine, bankLine] = convertBankTxToLedgerLines(tx);

        if (catLine.account_id === selectedAccountId) {
          open += catLine.debit - catLine.credit;
        }
        if (bankLine.account_id === selectedAccountId) {
          open += bankLine.debit - bankLine.credit;
        }
      }

      setOpeningBalance(open);

      // Period rows from journals
      const journalRows = (jData || []).map((e) => ({
        date: e.date,
        description: e.description,
        debit: normalizeMoney(e.money_in),
        credit: normalizeMoney(e.money_out),
        source: "Journal",
        source_id: e.id,
      }));

      // Period rows from bank (convert to 2 lines, keep only selectedAccountId)
      const bankLines = [];
      for (const tx of bData || []) {
        const [catLine, bankLine] = convertBankTxToLedgerLines(tx);
        if (catLine.account_id === selectedAccountId) bankLines.push(catLine);
        if (bankLine.account_id === selectedAccountId) bankLines.push(bankLine);
      }

      // Combine + apply source/search filters
      let combined = [...journalRows, ...bankLines];

      if (selectedSource)
        combined = combined.filter((r) => r.source === selectedSource);

      if (searchText.trim()) {
        const s = searchText.trim().toLowerCase();
        combined = combined.filter((r) =>
          String(r.description || "")
            .toLowerCase()
            .includes(s)
        );
      }

      // Sort by date then by source_id
      combined.sort((a, b) => {
        if (a.date === b.date)
          return String(a.source_id).localeCompare(String(b.source_id));
        return new Date(a.date) - new Date(b.date);
      });

      setRows(combined);
    } catch (e) {
      console.error("AccountLedger fetch error:", e);
      setErr("Failed to load account ledger.");
      setRows([]);
      setOpeningBalance(0);
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    selectedAccountId,
    filterStartDate,
    filterEndDate,
    selectedSource,
    searchText,
    convertBankTxToLedgerLines,
  ]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // Running balance
  const displayRows = useMemo(() => {
    let running = normalizeMoney(openingBalance);
    return rows.map((r) => {
      running += normalizeMoney(r.debit) - normalizeMoney(r.credit);
      return { ...r, balance: running };
    });
  }, [rows, openingBalance]);

  const totals = useMemo(() => {
    const debit = displayRows.reduce((s, r) => s + normalizeMoney(r.debit), 0);
    const credit = displayRows.reduce(
      (s, r) => s + normalizeMoney(r.credit),
      0
    );
    const closing = displayRows.length
      ? displayRows[displayRows.length - 1].balance
      : openingBalance;
    return { debit, credit, closing };
  }, [displayRows, openingBalance]);

  const selectedAccountName = useMemo(() => {
    const a = accounts?.find((x) => x.id === selectedAccountId);
    return a?.name || "";
  }, [accounts, selectedAccountId]);

  const exportCSV = () => {
    if (!selectedAccountId) return alert("Select an account first.");

    const payload = [
      {
        Date: "",
        Description: "OPENING BALANCE",
        Debit: "",
        Credit: "",
        Balance: openingBalance.toFixed(2),
        Source: "",
      },
      ...displayRows.map((r) => ({
        Date: formatDate(r.date),
        Description: r.description,
        Debit: normalizeMoney(r.debit).toFixed(2),
        Credit: normalizeMoney(r.credit).toFixed(2),
        Balance: normalizeMoney(r.balance).toFixed(2),
        Source: r.source,
      })),
    ];

    const csv = Papa.unparse(payload);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `account_ledger_${selectedAccountName || "account"}.csv`);
  };

  return (
    <div className="al-container">
      <div className="al-header">
        <div>
          <h2 className="al-title">Account Ledger</h2>
          <p className="al-sub">
            Per-account ledger (Journal + Bank double-entry)
          </p>
        </div>
        <button className="al-primary" onClick={exportCSV}>
          Export CSV
        </button>
      </div>

      <div className="al-filter-card">
        <div className="al-row">
          <div className="input-group">
            <label>Account</label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
            >
              <option value="">Select</option>
              {(accounts || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.category ? `(${a.category})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>End Date</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Source</label>
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
            >
              <option value="">All</option>
              <option value="Journal">Journal</option>
              <option value="Bank">Bank</option>
            </select>
          </div>

          <div className="input-group wide">
            <label>Search</label>
            <input
              placeholder="Search narration/description…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>&nbsp;</label>
            <button className="al-primary" onClick={fetchLedger}>
              Apply Filters
            </button>
          </div>
        </div>

        {err && <p className="al-error">{err}</p>}
      </div>

      <div className="al-summary">
        <div className="pill">
          <span className="muted">Opening</span>
          <strong>₦{normalizeMoney(openingBalance).toLocaleString()}</strong>
        </div>
        <div className="pill">
          <span className="muted">Total Debit</span>
          <strong>₦{normalizeMoney(totals.debit).toLocaleString()}</strong>
        </div>
        <div className="pill">
          <span className="muted">Total Credit</span>
          <strong>₦{normalizeMoney(totals.credit).toLocaleString()}</strong>
        </div>
        <div className="pill primary">
          <span className="muted">Closing</span>
          <strong>₦{normalizeMoney(totals.closing).toLocaleString()}</strong>
        </div>
      </div>

      <div className="al-card">
        {loading ? (
          <div className="al-loading">Loading…</div>
        ) : !selectedAccountId ? (
          <div className="al-empty">Select an account to view its ledger.</div>
        ) : (
          <div className="al-table-wrap">
            <table className="al-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="num">Debit (₦)</th>
                  <th className="num">Credit (₦)</th>
                  <th className="num">Balance (₦)</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                <tr className="opening-row">
                  <td>—</td>
                  <td>
                    <strong>OPENING BALANCE</strong>
                  </td>
                  <td className="num">—</td>
                  <td className="num">—</td>
                  <td className="num">
                    <strong>
                      ₦{normalizeMoney(openingBalance).toLocaleString()}
                    </strong>
                  </td>
                  <td>—</td>
                </tr>

                {displayRows.length ? (
                  displayRows.map((r) => (
                    <tr key={`${r.source}-${r.source_id}-${r.date}`}>
                      <td>{formatDate(r.date)}</td>
                      <td className="desc">{r.description}</td>
                      <td className="num">
                        {normalizeMoney(r.debit)
                          ? normalizeMoney(r.debit).toLocaleString()
                          : "—"}
                      </td>
                      <td className="num">
                        {normalizeMoney(r.credit)
                          ? normalizeMoney(r.credit).toLocaleString()
                          : "—"}
                      </td>
                      <td className="num">
                        {normalizeMoney(r.balance).toLocaleString()}
                      </td>
                      <td>{r.source}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="empty">
                      No ledger entries found for selected filters.
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan="2" className="total-label">
                    <strong>Totals</strong>
                  </td>
                  <td className="num">
                    <strong>
                      {normalizeMoney(totals.debit).toLocaleString()}
                    </strong>
                  </td>
                  <td className="num">
                    <strong>
                      {normalizeMoney(totals.credit).toLocaleString()}
                    </strong>
                  </td>
                  <td className="num">
                    <strong>
                      {normalizeMoney(totals.closing).toLocaleString()}
                    </strong>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
